import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25States } from "../../../services/state/state.map";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import SeOrg = StudentEngagementOrg.SeOrg;
import { StudentEngagementUtil } from "../student.engagement.util";

@TypeManagerDecorator("se-organization-tile")
@Component({
    selector: "se-organization-tile",
    template: `
        <a s25-ng-router-link [stateName]="routingStateName" [stateParams]="stateParams()">
            <div class="org-tile-wrapper">
                <div class="org-image-container">
                    @if (logoUrl()) {
                        <img [src]="logoUrl()" [alt]="org().itemName + ' logo'" class="org-image" />
                    } @else {
                        <div class="org-image-placeholder">
                            <s25-ng-icon [type]="'organization'" [size]="80"></s25-ng-icon>
                        </div>
                    }
                </div>
                <div class="org-details">
                    <h3 class="org-name">{{ displayName() }}</h3>
                    @if (org()?.mission) {
                        <p class="org-mission">{{ org().mission }}</p>
                    }
                </div>
            </div>
        </a>
    `,
    styles: `
        .org-tile-wrapper {
            background: #ffffff;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            display: flex;
            align-items: flex-start;
            height: 100%;
            transition: all 0.3s ease;
        }

        a {
            text-decoration: none;
            color: inherit;
            display: block;
            height: 100%;
        }

        a:hover .org-tile-wrapper {
            border-color: #94a3b8;
            box-shadow: 0 4px 12px rgba(148, 163, 184, 0.2);
            transform: translateY(-2px);
        }

        .org-image-container {
            flex-shrink: 0;
            width: 120px;
            height: 120px;
            border-right: 1px solid #e0e0e0;
            border-radius: 8px;
            /*padding: 2px;*/
        }

        .org-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 8px;
        }

        .org-image-placeholder {
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
        }

        .org-details {
            flex: 1;
            min-width: 0;
            padding: 1em;
        }

        .org-name {
            font-size: 18px;
            font-weight: 600;
            color: #333;
            margin: 0 0 8px 0;
        }

        .org-mission {
            font-size: 14px;
            color: #666;
            margin: 0;
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25IconComponent, S25RouterLinkDirective],
})
export class SeOrganizationTileComponent {
    org = input.required<SeOrg>();

    routingStateName = S25States.names.studentEngagementOrganization;
    stateParams = computed(() => ({ itemId: this.org().itemId }));

    /**
     * Get the logo image URL from the FileStore custom attribute value
     */
    logoUrl = computed(() => StudentEngagementUtil.parseUrl(this.org()?.logoUrl));

    /**
     * Display name - prefer title over itemName
     */
    displayName = computed(() => {
        return this.org()?.title || this.org().itemName;
    });
}
